import { CKIconGradient } from '@clearkit/icons';
import MuiThemeProvider from '@material-ui/core/styles/MuiThemeProvider';
import queryString from 'query-string';
import { lazy, Suspense } from 'react';
import { ApolloProvider } from 'react-apollo';
import { Helmet } from 'react-helmet';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { Transition, TransitionGroup } from 'react-transition-group';
import { Provider } from 'unstated';

import MissingPage from '~/components/MissingPage';
import NPS from '~/components/NPS';
import SegmentsMenu from '~/components/SegmentsMenu';
import SidebarDrawer from '~/components/SidebarDrawer';
import ToastStack from '~/components/ToastStack';
import apollo from '~/lib/apollo';
import history from '~/lib/history';
import paths from '~/lib/paths';
import { segmentTypes } from '~/lib/segments';
const Audience = lazy(() => import('~/pages/audiences/Show'));
const AudienceEdit = lazy(() => import('~/pages/audiences/Edit'));
const AudienceNew = lazy(() => import('~/pages/audiences/New'));
const Audiences = lazy(() => import('~/pages/audiences/List'));
const AudiencesAccess = lazy(() => import('~/pages/audiences/Access'));
const Companies = lazy(() => import('~/pages/companies/List'));
const CompaniesSegment = lazy(() => import('~/pages/companies/Segment'));
import Company from '~/pages/company';
const AccountConnector = lazy(() =>
  import('~/pages/configurations/Show/AccountConnector'),
);
const Disconnected = lazy(() =>
  import('~/pages/configurations/Show/Disconnected'),
);
const JavaScript = lazy(() => import('~/pages/configurations/Show/JavaScript'));
const Segment = lazy(() => import('~/pages/configurations/Show/Segment'));
const Configurations = lazy(() => import('~/pages/configurations/List'));
const ContactSuccess = lazy(() => import('~/pages/ContactSuccess'));
import ImportCsv from '~/pages/CsvImport';
import MapCsv from '~/pages/CsvImport/Map';
const Dashboard = lazy(() => import('~/pages/Dashboard'));
import NewsDetail from '~/pages/NewsDetail';
import NoAccess from '~/pages/NoAccess';
import LegacyPerson from '~/pages/people/LegacyShow';
const People = lazy(() => import('~/pages/people/List'));
const PeopleSegment = lazy(() => import('~/pages/people/Segment'));
import Person from '~/pages/people/Show';
const Sandbox = lazy(() => import('~/pages/Sandbox'));
import SegmentDestinationEdit from '~/pages/destinations/Edit';
import SegmentDestinations from '~/pages/destinations/List';
import SegmentDestinationNew from '~/pages/destinations/New';
const SegmentEdit = lazy(() => import('~/pages/SegmentEdit'));
const SegmentNew = lazy(() => import('~/pages/SegmentNew'));
const Welcome = lazy(() => import('~/pages/Welcome'));

import Forms from '~/pages/forms/Forms';

import styles from './App.style';
import Gatekeeper from './app/Gatekeeper';
import Sidebar from './app/Sidebar';
import theme from './app/theme';
import ErrorHandler from './errorHandler';
import GatedRoute from './GatedRoute';

const UUIDRegex =
  '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

import UNSTATED from 'unstated-debug';

import {
  userHasAudiencesAccess,
  userHasCompaniesAccess,
  userHasDashboardAccess,
  userHasInternalAccess,
  userHasPeopleAccess,
  userHasSettingsAccess,
  userHasSetupAccess,
} from '~/lib/gates';

import LoadingArea from './LoadingArea';

UNSTATED.enabled = process.env.NODE_ENV === 'development';
UNSTATED.logStateChanges = false;

class App extends React.Component {
  render() {
    const { classes } = this.props;

    return (
      <ApolloProvider client={apollo}>
        <Provider>
          <Helmet
            defaultTemplate="Clearbit X"
            titleTemplate="%s – Clearbit X"
          />

          <CKIconGradient />
          <ToastStack />

          <Router history={history}>
            <MuiThemeProvider theme={theme}>
              <Gatekeeper />

              <NPS />

              <div className={classes.root}>
                <Sidebar />

                <ErrorHandler>
                  <article className={classes.article}>
                    <GatedRoute
                      path={[`/people/segments/:id(${UUIDRegex})`, '/people']}
                      gateFunction={userHasPeopleAccess}
                      render={({
                        match: {
                          params: { id },
                        },
                      }) => (
                        <SegmentsMenu
                          type="Person"
                          segmentId={id}
                          key="person"
                        />
                      )}
                    />
                    <GatedRoute
                      path={[
                        `/companies/segments/:id(${UUIDRegex})`,
                        '/companies',
                      ]}
                      gateFunction={userHasCompaniesAccess}
                      render={({
                        match: {
                          params: { id },
                        },
                      }) => (
                        <SegmentsMenu
                          type="Company"
                          segmentId={id}
                          key="company"
                        />
                      )}
                    />

                    <Suspense fallback={<LoadingArea loading />}>
                      <Switch>
                        <Route exact path="/no-access" component={NoAccess} />

                        <GatedRoute
                          exact
                          path={['/companies/import']}
                          gateFunction={userHasCompaniesAccess}
                          render={() => (
                            <ImportCsv key="Company" type="Company" />
                          )}
                        />

                        <GatedRoute
                          exact
                          path={['/people/import']}
                          gateFunction={userHasPeopleAccess}
                          render={() => (
                            <ImportCsv key="Person" type="Person" />
                          )}
                        />

                        <GatedRoute
                          exact
                          path={[`/companies/import/:id(${UUIDRegex})`]}
                          gateFunction={userHasCompaniesAccess}
                          render={({
                            match: {
                              params: { id },
                            },
                          }) => <MapCsv key="Company" id={id} type="Company" />}
                        />
                        <GatedRoute
                          exact
                          path={[`/people/import/:id(${UUIDRegex})`]}
                          gateFunction={userHasPeopleAccess}
                          render={({
                            match: {
                              params: { id },
                            },
                          }) => <MapCsv key="Person" id={id} type="Person" />}
                        />

                        <GatedRoute
                          exact
                          path="/audiences"
                          fallbackPath={paths.audiencesAccess()}
                          component={Audiences}
                          gateFunction={userHasAudiencesAccess}
                        />

                        <Route
                          path={[
                            '/audiences/access/request',
                            '/audiences/access',
                          ]}
                          render={({ match: { path } }) => (
                            <TransitionGroup>
                              <Transition
                                key={path}
                                unmountOnExit
                                timeout={500}>
                                {(state) => (
                                  <>
                                    <SidebarDrawer
                                      open={
                                        path.includes('/request') &&
                                        state === 'entered'
                                      }
                                      onClose={() => {
                                        history.push(paths.audiencesAccess());
                                      }}>
                                      <ContactSuccess
                                        product="advertising"
                                        backPath={paths.audiencesAccess()}
                                      />
                                    </SidebarDrawer>
                                  </>
                                )}
                              </Transition>
                              <AudiencesAccess />
                            </TransitionGroup>
                          )}
                        />

                        <GatedRoute
                          exact
                          path="/audiences/new/:audienceSubtype(company-segment-prospects|contacts|managed-contacts|managed-visitors|prospects|visitors)?/:step(sync)?"
                          fallbackPath={paths.audiencesAccess()}
                          gateFunction={userHasAudiencesAccess}
                          render={({
                            match: {
                              params: { audienceSubtype, step },
                            },
                            location,
                          }) => {
                            const {
                              segmentId,
                              segmentType,
                            } = queryString.parse(location.search);

                            const stepIndex = [audienceSubtype, step].filter(
                              Boolean,
                            ).length;

                            return (
                              <AudienceNew
                                key={audienceSubtype}
                                segmentId={segmentId}
                                segmentType={segmentType}
                                stepIndex={stepIndex}
                                subtype={audienceSubtype?.replace(/-/g, '_')}
                              />
                            );
                          }}
                        />

                        <GatedRoute
                          exact
                          path={`/audiences/:id(${UUIDRegex})`}
                          fallbackPath={paths.audiencesAccess()}
                          gateFunction={userHasAudiencesAccess}
                          render={({
                            match: {
                              params: { id },
                            },
                          }) => <Audience id={id} />}
                        />

                        <GatedRoute
                          exact
                          path="/audiences/:id/edit"
                          fallbackPath={paths.audiencesAccess()}
                          gateFunction={userHasAudiencesAccess}
                          render={({
                            match: {
                              params: { id },
                            },
                          }) => <AudienceEdit id={id} />}
                        />

                        <GatedRoute
                          exact
                          path={`/companies/:companyId(${UUIDRegex})?`}
                          component={Companies}
                          gateFunction={userHasCompaniesAccess}
                        />

                        <GatedRoute
                          exact
                          path="/companies/segments/new"
                          gateFunction={userHasCompaniesAccess}
                          render={({
                            history: {
                              location: { state = {} },
                            },
                          }) => {
                            const { collection_id: collectionId } = state;

                            return (
                              <SegmentNew
                                collectionId={collectionId}
                                key={collectionId}
                                type="Company"
                              />
                            );
                          }}
                        />

                        <GatedRoute
                          exact
                          path={`/companies/segments/:id(${UUIDRegex})/edit`}
                          gateFunction={userHasCompaniesAccess}
                          render={({
                            match: {
                              params: { id },
                            },
                          }) => (
                            <SegmentEdit
                              id={id}
                              type={segmentTypes.company}
                              attributeType="Company"
                              collectionType="Company"
                              legacyType="Company"
                            />
                          )}
                        />

                        <GatedRoute
                          exact
                          path={[
                            `/companies/segments/:segmentId(${UUIDRegex})/destinations/:id(${UUIDRegex})?/:action?`,
                            `/companies/segments/:segmentId(${UUIDRegex})/destinations/:action?/:product?`,
                          ]}
                          gateFunction={userHasCompaniesAccess}
                          render={({
                            match: {
                              params: { action, id, product, segmentId },
                            },
                            location,
                          }) => {
                            const searchParams = new URLSearchParams(
                              location.search,
                            );

                            return (
                              <>
                                <SegmentDestinations
                                  segmentId={segmentId}
                                  segmentType={segmentTypes.company}
                                />
                                <SidebarDrawer
                                  open={!!action}
                                  onClose={() => {
                                    history.push(
                                      paths.segmentDestinations(
                                        segmentTypes.company,
                                        segmentId,
                                      ),
                                    );
                                  }}>
                                  {(() => {
                                    switch (action) {
                                      case 'new':
                                        return (
                                          <SegmentDestinationNew
                                            segmentId={segmentId}
                                            segmentType={segmentTypes.company}
                                            type={searchParams.get('type')}
                                          />
                                        );
                                      case 'edit':
                                        return (
                                          <SegmentDestinationEdit
                                            segmentId={segmentId}
                                            segmentType={segmentTypes.company}
                                            destinationId={id}
                                          />
                                        );
                                      case 'contact-success':
                                        return (
                                          <ContactSuccess
                                            product={product}
                                            backPath={paths.segmentDestinations(
                                              segmentTypes.company,
                                              segmentId,
                                            )}
                                          />
                                        );
                                      default:
                                        // A default is needed when the segment is animating in and out
                                        return (
                                          <SegmentDestinationNew
                                            segmentId={segmentId}
                                            segmentType={segmentTypes.company}
                                          />
                                        );
                                    }
                                  })()}
                                </SidebarDrawer>
                              </>
                            );
                          }}
                        />

                        <GatedRoute
                          exact
                          path={`/companies/segments/:id(${UUIDRegex})/:personId(${UUIDRegex})?`}
                          gateFunction={userHasCompaniesAccess}
                          render={({
                            match: {
                              params: { id },
                            },
                          }) => <CompaniesSegment id={id} key={id} />}
                        />

                        <Redirect
                          exact
                          from={`/companies/segments/:segmentId(${UUIDRegex})/destinations/:id(${UUIDRegex})`}
                          to="/companies/segments/:segmentId/destinations/:id/edit"
                        />

                        <Route
                          path="/forms/:id?/:action?"
                          render={({
                            match: {
                              params: { action, id },
                            },
                          }) => <Forms action={action} id={id} />}
                        />

                        <GatedRoute
                          exact
                          path={`/people/:personId(${UUIDRegex})?`}
                          component={People}
                          gateFunction={userHasPeopleAccess}
                        />

                        <GatedRoute
                          exact
                          path="/people/segments/new"
                          gateFunction={userHasPeopleAccess}
                          render={({
                            history: {
                              location: { state = {} },
                            },
                          }) => {
                            const { collection_id: collectionId } = state;

                            return (
                              <SegmentNew
                                collectionId={collectionId}
                                key={collectionId}
                                type="Person"
                              />
                            );
                          }}
                        />

                        <GatedRoute
                          exact
                          path={`/people/segments/:id(${UUIDRegex})/edit`}
                          gateFunction={userHasPeopleAccess}
                          render={({
                            match: {
                              params: { id },
                            },
                          }) => (
                            <SegmentEdit
                              id={id}
                              type={segmentTypes.person}
                              attributeType="Person"
                              collectionType="Person"
                              legacyType="Person"
                            />
                          )}
                        />

                        <GatedRoute
                          exact
                          path={[
                            `/people/segments/:segmentId(${UUIDRegex})/destinations/:id(${UUIDRegex})?/:action?`,
                            `/people/segments/:segmentId(${UUIDRegex})/destinations/:action?/:product`,
                          ]}
                          gateFunction={userHasPeopleAccess}
                          render={({
                            match: {
                              params: { action, id, product, segmentId },
                            },
                            location,
                          }) => {
                            const searchParams = new URLSearchParams(
                              location.search,
                            );

                            return (
                              <>
                                <SegmentDestinations
                                  segmentId={segmentId}
                                  segmentType={segmentTypes.person}
                                />
                                <SidebarDrawer
                                  open={!!action}
                                  onClose={() =>
                                    history.push(
                                      paths.segmentDestinations(
                                        segmentTypes.person,
                                        segmentId,
                                      ),
                                    )
                                  }>
                                  {(() => {
                                    switch (action) {
                                      case 'new':
                                        return (
                                          <SegmentDestinationNew
                                            segmentId={segmentId}
                                            segmentType={segmentTypes.person}
                                            type={searchParams.get('type')}
                                          />
                                        );
                                      case 'edit':
                                        return (
                                          <SegmentDestinationEdit
                                            segmentId={segmentId}
                                            segmentType={segmentTypes.person}
                                            destinationId={id}
                                          />
                                        );
                                      case 'contact-success':
                                        return (
                                          <ContactSuccess
                                            product={product}
                                            backPath={paths.segmentDestinations(
                                              segmentTypes.person,
                                              segmentId,
                                            )}
                                          />
                                        );
                                      default:
                                        // A default is needed when the segment is animating in and out
                                        return (
                                          <SegmentDestinationNew
                                            segmentId={segmentId}
                                            segmentType={segmentTypes.person}
                                          />
                                        );
                                    }
                                  })()}
                                </SidebarDrawer>
                              </>
                            );
                          }}
                        />

                        <GatedRoute
                          exact
                          path={`/people/segments/:id(${UUIDRegex})/:personId(${UUIDRegex})?`}
                          gateFunction={userHasPeopleAccess}
                          render={({
                            match: {
                              params: { id },
                            },
                          }) => <PeopleSegment id={id} key={id} />}
                        />

                        <Redirect
                          exact
                          from={`/people/segments/:segmentId(${UUIDRegex})/destinations/:id(${UUIDRegex})`}
                          to="/people/segments/:segmentId/destinations/:id/edit"
                        />

                        <GatedRoute
                          path="/settings/configuration/javascript"
                          gateFunction={userHasSettingsAccess}>
                          <JavaScript />
                        </GatedRoute>

                        <GatedRoute
                          path="/settings/configuration/segment"
                          gateFunction={userHasSettingsAccess}>
                          <Segment />
                        </GatedRoute>

                        <GatedRoute
                          path="/settings/configuration/:service/connect"
                          gateFunction={userHasSettingsAccess}
                          render={({
                            match: {
                              params: { service },
                            },
                          }) => <Disconnected service={service} />}
                        />

                        <GatedRoute
                          path="/settings/configuration/:service"
                          gateFunction={userHasSettingsAccess}
                          render={({
                            match: {
                              params: { service },
                            },
                          }) => <AccountConnector service={service} />}
                        />

                        <GatedRoute
                          path="/settings/configuration"
                          gateFunction={userHasSettingsAccess}>
                          <Configurations />
                        </GatedRoute>

                        <GatedRoute
                          exact
                          path="/welcome"
                          component={Welcome}
                          gateFunction={userHasSetupAccess}
                        />

                        <GatedRoute
                          exact
                          path={['/', '/news/:id']}
                          component={Dashboard}
                          gateFunction={userHasDashboardAccess}
                        />

                        <GatedRoute
                          path="/sandbox/:component?"
                          component={Sandbox}
                          gateFunction={userHasInternalAccess}
                        />

                        <Route>
                          <MissingPage />
                        </Route>
                      </Switch>
                    </Suspense>

                    {/* Sidebars */}
                    <Switch>
                      <Route
                        path={[
                          `/companies/segments/:segmentId/:id(${UUIDRegex})`,
                          `/people/segments/:segmentId/:id(${UUIDRegex})`,
                          `/companies/:id(${UUIDRegex})`,
                          `/people/:id(${UUIDRegex})`,
                          `/people-new/:id(${UUIDRegex})`,
                          '/companies',
                          '/people',
                        ]}
                        render={({ location, match }) => (
                          <TransitionGroup>
                            <Transition
                              key={match.path}
                              unmountOnExit
                              timeout={500}>
                              {(state) => (
                                <Switch location={location}>
                                  <GatedRoute
                                    exact
                                    gateFunction={userHasCompaniesAccess}
                                    path={[
                                      `/companies/:id(${UUIDRegex})`,
                                      `/companies/segments/:segmentId(${UUIDRegex})/:id(${UUIDRegex})`,
                                    ]}
                                    render={({
                                      match: {
                                        params: { id, segmentId },
                                      },
                                    }) => (
                                      <SidebarDrawer
                                        open={state === 'entered'}
                                        onClose={() => {
                                          history.push(
                                            paths.segment(
                                              segmentTypes.company,
                                              segmentId,
                                            ),
                                          );
                                        }}>
                                        <Company id={id} />
                                      </SidebarDrawer>
                                    )}
                                  />

                                  <GatedRoute
                                    exact
                                    gateFunction={userHasPeopleAccess}
                                    path={[
                                      `/people/:id(${UUIDRegex})`,
                                      `/people/segments/:segmentId(${UUIDRegex})/:id(${UUIDRegex})`,
                                    ]}
                                    render={({
                                      match: {
                                        params: { id, segmentId },
                                      },
                                    }) => (
                                      <SidebarDrawer
                                        open={state === 'entered'}
                                        onClose={() => {
                                          history.push(
                                            paths.segment(
                                              segmentTypes.person,
                                              segmentId,
                                            ),
                                          );
                                        }}>
                                        <LegacyPerson id={id} />
                                      </SidebarDrawer>
                                    )}
                                  />

                                  <GatedRoute
                                    exact
                                    gateFunction={userHasPeopleAccess}
                                    path={[
                                      `/people-new/:id(${UUIDRegex})`,
                                      `/people-new/segments/:segmentId(${UUIDRegex})/:id(${UUIDRegex})`,
                                    ]}
                                    render={({
                                      match: {
                                        params: { id, segmentId },
                                      },
                                    }) => (
                                      <SidebarDrawer
                                        open={state === 'entered'}
                                        onClose={() => {
                                          history.push(
                                            paths.segment(
                                              segmentTypes.person,
                                              segmentId,
                                            ),
                                          );
                                        }}>
                                        <Person id={id} />
                                      </SidebarDrawer>
                                    )}
                                  />
                                </Switch>
                              )}
                            </Transition>
                          </TransitionGroup>
                        )}
                      />

                      <GatedRoute
                        exact
                        path={['/news/:id', '/']}
                        render={({
                          match: {
                            path,
                            params: { id },
                          },
                        }) => (
                          <TransitionGroup>
                            <Transition key={path} unmountOnExit timeout={500}>
                              {(state) => (
                                <SidebarDrawer
                                  onClose={() => {
                                    history.push('/');
                                  }}
                                  open={!!id && state === 'entered'}>
                                  <NewsDetail id={id} />
                                </SidebarDrawer>
                              )}
                            </Transition>
                          </TransitionGroup>
                        )}
                        gateFunction={userHasDashboardAccess}
                      />
                    </Switch>
                  </article>
                </ErrorHandler>
              </div>
            </MuiThemeProvider>
            <Route
              path="/"
              render={() => {
                if (
                  window.analytics &&
                  typeof window.analytics.page === 'function'
                ) {
                  window.analytics.page(history.location.pathname);
                }
                return null;
              }}
            />
          </Router>
        </Provider>
      </ApolloProvider>
    );
  }
}

App.propTypes = {
  classes: PropTypes.object.isRequired,
};

const StyledApp = withStyles(styles)(App);
export default StyledApp;
